/** Small shared pieces of the design: icons, the toast, loading text, "time ago" formatting. */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

export function Icon({ id, className = 'icon', style }: { id: string; className?: string; style?: React.CSSProperties }) {
  return <svg className={className} aria-hidden="true" style={style}><use href={`#${id}`} /></svg>
}

/** Raw design sprite (from src/design/sprites.ts) rendered once per page root. */
export function Sprite({ svg }: { svg: string }) {
  return <div style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: svg }} />
}

const ToastCtx = createContext<(msg: string) => void>(() => {})
export const useToast = () => useContext(ToastCtx)
export function ToastHost({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState('')
  const [show, setShow] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const toast = useCallback((m: string) => {
    setMsg(m); setShow(true)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setShow(false), 2600)
  }, [])
  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div className={`toast${show ? ' show' : ''}`} role="status" aria-live="polite">{msg}</div>
    </ToastCtx.Provider>
  )
}

export function shortTime(iso?: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  const y = new Date(now); y.setDate(now.getDate() - 1)
  if (d.toDateString() === y.toDateString()) return 'Yesterday'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
export function longTime(iso?: string | null) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export function displayName(user: any) {
  const f = user?.firstName || user?.first_name || ''
  const l = user?.lastName || user?.last_name || ''
  return `${f} ${l}`.trim() || user?.username || ''
}

/**
 * Opens a <dialog> as a modal once it is on the page. No close() on unmount: React's development double-mount
 * (StrictMode) would fire the dialog's "close" event and the parent would treat that as "the fan closed it".
 * When the dialog leaves the page the browser removes it from the top layer by itself.
 */
export function useShowModal(ref: React.RefObject<HTMLDialogElement>) {
  useEffect(() => { const d = ref.current; if (d && !d.open) d.showModal() }, [])
}
